<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import FollowUp from 'dashboard/components-next/FollowUp/FollowUp.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();

const CHANNEL_LABELS = {
  'Channel::WebWidget': 'Website',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::FacebookPage': 'Facebook',
  'Channel::Instagram': 'Instagram',
  'Channel::Email': 'Email',
  'Channel::Api': 'API',
  'Channel::TwilioSms': 'SMS',
  'Channel::Telegram': 'Telegram',
  'Channel::Line': 'Line',
  'Channel::Sms': 'SMS',
  'Channel::Tiktok': 'TikTok',
  'Channel::Voip': 'Voice',
};
const VERIFICATION_COLORS = {
  'Pending Review': 'amber',
  Verified: 'teal',
  Junk: 'ruby',
};
const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};

const sourceChannel = computed(() => props.contact.source_channel || '');
const channelIcon = computed(() =>
  sourceChannel.value ? getInboxIconByType(sourceChannel.value) : null
);
const channelLabel = computed(
  () =>
    CHANNEL_LABELS[sourceChannel.value] ||
    sourceChannel.value?.replace('Channel::', '') ||
    ''
);

const stage = computed(() => props.contact.custom_attributes?.stage || '');

const verificationStatus = computed(
  () => props.contact.custom_attributes?.verification_status || ''
);
const verificationColor = computed(
  () => VERIFICATION_COLORS[verificationStatus.value] || 'slate'
);

const priority = computed(() => props.contact.priority || '');
const priorityLabel = computed(() =>
  priority.value ? t(PRIORITY_LABEL_KEYS[priority.value]) : ''
);

const companyName = computed(
  () => props.contact.additional_attributes?.company_name || ''
);

const captainSummarySnippet = computed(() => {
  if (!props.contact.captain_summary) return '';
  const lines = props.contact.captain_summary
    .split('\n')
    .filter(Boolean)
    .slice(0, 2);
  return lines.join(' ');
});

const assignee = computed(() => props.contact.assignee || null);

const nextFollowUpAt = computed(
  () => props.contact.custom_attributes?.next_follow_up_at || ''
);

const formattedLastConversationAt = computed(() =>
  props.contact.last_conversation_at
    ? dynamicTime(props.contact.last_conversation_at)
    : ''
);

const openContact = () => {
  router.push({
    name: 'contacts_edit',
    params: { contactId: props.contact.id },
  });
};
</script>

<template>
  <div
    class="relative flex flex-col flex-shrink-0 overflow-hidden transition-all duration-150 border rounded-lg group/card bg-n-solid-2 border-n-weak hover:border-n-slate-6 hover:shadow-md hover:-translate-y-0.5"
  >
    <div
      class="flex items-center justify-center kanban-drag-handle h-3.5 cursor-grab active:cursor-grabbing hover:bg-n-alpha-1"
    >
      <Icon
        icon="i-lucide-grip-horizontal"
        class="text-n-slate-6 group-hover/card:text-n-slate-8 size-3"
      />
    </div>

    <div
      class="flex flex-col gap-3 px-4 pt-1 pb-4 cursor-pointer"
      @click="openContact"
    >
      <div class="flex items-start gap-2.5">
        <Avatar :name="contact.name" :src="contact.thumbnail" :size="38" />
        <div class="flex flex-col min-w-0 gap-1">
          <span
            class="text-[14.5px] font-semibold leading-tight truncate text-n-slate-12"
          >
            {{ contact.name }}
          </span>
          <div class="flex items-center flex-wrap gap-1.5 min-w-0">
            <span v-if="companyName" class="text-xs truncate text-n-slate-10">
              {{ companyName }}
            </span>
            <span
              v-if="sourceChannel"
              class="inline-flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded text-n-slate-10 bg-n-alpha-2"
            >
              <Icon :icon="channelIcon" class="size-2.5" />
              {{ channelLabel }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-1.5">
        <span v-if="priority" class="inline-flex items-center">
          <Label :label="priorityLabel" color="slate" compact>
            <template #icon>
              <CardPriorityIcon :priority="priority" class="!size-3" />
            </template>
          </Label>
        </span>
        <span v-if="verificationStatus" class="inline-flex items-center">
          <Label
            :label="verificationStatus"
            :color="verificationColor"
            compact
          />
        </span>
        <span v-if="stage" class="inline-flex items-center">
          <Label :label="stage" color="iris" compact />
        </span>
      </div>

      <div
        v-if="captainSummarySnippet"
        class="flex flex-col gap-1 px-2.5 py-2 rounded-lg bg-n-alpha-1"
      >
        <span
          class="text-[10px] font-bold tracking-wide uppercase text-n-slate-9"
        >
          {{ t('KANBAN.CARD.CAPTAIN_SUMMARY') }}
        </span>
        <p class="text-xs italic leading-relaxed text-n-slate-11 line-clamp-2">
          {{ captainSummarySnippet }}
        </p>
      </div>

      <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
        <div
          v-if="assignee"
          class="flex items-center gap-2 text-xs text-n-slate-11"
        >
          <span class="w-16 text-n-slate-9">
            {{ t('KANBAN.CARD.AGENT') }}
          </span>
          <Avatar :name="assignee.name" :src="assignee.thumbnail" :size="16" />
          {{ assignee.name }}
        </div>
        <FollowUp
          v-if="nextFollowUpAt"
          mode="compact"
          :contact-id="contact.id"
          :next-follow-up-at="nextFollowUpAt"
        />
        <div
          v-if="formattedLastConversationAt"
          class="flex items-center gap-2 text-xs text-n-slate-11"
        >
          <span class="w-16 text-n-slate-9">
            {{ t('KANBAN.CARD.LAST_CONVERSATION_LABEL') }}
          </span>
          {{ formattedLastConversationAt }}
        </div>
      </div>
    </div>
  </div>
</template>
